import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowRight, ArrowLeftRight, Filter, TrendingUp, Shield,
  Search, Coins, Key, FileSearch, Shuffle,
  CircleDollarSign, Cog, Rocket, Building2, Lightbulb, Landmark,
  ChevronRight, CheckCircle2, XCircle, Clock
} from 'lucide-react';
import { 
  trustSignals, problemPoints, solutionPoints, howItWorksSteps, 
  audienceTypes, traditionalModel, ipfundersModel, pricingPhilosophy
} from '../data/mock';

const iconMap = {
  Filter, TrendingUp, ArrowLeftRight, Shield, Search, Coins, Key,
  FileSearch, CircleDollarSign, Cog, Rocket, Building2, Lightbulb, Landmark,
  Clock, Shuffle
};

const LandingPage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-50 to-white pt-20 pb-32">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#1e3a5f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
          <svg className="absolute inset-0 w-full h-full opacity-[0.02]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#1e3a5f" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f1f2e] leading-tight mb-6" data-testid="hero-headline">
              We fund IP protection.
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-10 max-w-3xl mx-auto" data-testid="hero-subheadline">
              We cover the upfront cost of protection and align investment with progress.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/request-access">
                <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2a4a73] text-white px-8 py-6 text-base shadow-xl shadow-[#1e3a5f]/20 transition-all duration-300 hover:shadow-2xl hover:shadow-[#1e3a5f]/30 hover:-translate-y-0.5" data-testid="hero-cta-button">
                  Request Evaluation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base" data-testid="hero-how-it-works-button">
                  Learn How It Works
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bridging Paragraph */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="bridging-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-lg text-slate-600 leading-relaxed text-center" data-testid="bridging-paragraph">
            For most technology companies, IP protection begins with patents—front-loaded in cost, long in uncertainty, and difficult to justify before commercial value is clear. IpFunders exists to shift that timing risk by funding protection early and aligning commitment with progress, starting where protection most often begins.
          </p>
        </div>
      </section>

      {/* Value Props Strip */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trustSignals.map((signal) => {
              const Icon = iconMap[signal.icon];
              return (
                <div key={signal.id} className="flex items-start space-x-4 group">
                  <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#1e3a5f] transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#1e3a5f] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f1f2e] mb-1">{signal.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{signal.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Model Comparison */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              A Different Approach to IP
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Traditional models demand commitment before clarity. Ours doesn't.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Traditional Model */}
            <Card className="bg-white border-slate-200 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-red-400" />
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0f1f2e]">{traditionalModel.title}</h3>
                    <p className="text-sm text-red-600">{traditionalModel.subtitle}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {traditionalModel.points.map((point, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-4 bg-red-50/50 rounded-xl border border-red-100">
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-red-600 text-sm font-medium">{idx + 1}</span>
                      </div>
                      <div>
                        <div className="font-medium text-[#0f1f2e]">{point.label}</div>
                        <div className="text-sm text-slate-500">{point.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* IpFunders Model */}
            <Card className="bg-white border-slate-200 relative overflow-hidden shadow-xl">
              <div className="absolute top-0 left-0 right-0 h-1 bg-emerald-500" />
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0f1f2e]">{ipfundersModel.title}</h3>
                    <p className="text-sm text-emerald-600">{ipfundersModel.subtitle}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  {ipfundersModel.points.map((point, idx) => (
                    <div key={idx} className="flex items-start space-x-3 p-4 bg-emerald-50/50 rounded-xl border border-emerald-100">
                      <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="font-medium text-[#0f1f2e]">{point.label}</div>
                        <div className="text-sm text-slate-500">{point.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Philosophy Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              {pricingPhilosophy.headline}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {pricingPhilosophy.subheadline}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPhilosophy.principles.map((principle, idx) => {
              const Icon = iconMap[principle.icon];
              return (
                <Card key={idx} className="bg-slate-50 border-slate-200 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 bg-[#1e3a5f]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-7 h-7 text-[#1e3a5f]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0f1f2e] mb-3">{principle.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{principle.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-center text-sm text-slate-500 mt-10">
            {pricingPhilosophy.footnote}
          </p>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="py-24 bg-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>The Process</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Unfolds
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              A natural progression from early protection to strategic clarity.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {howItWorksSteps.map((item, idx) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.step} className="relative">
                  {idx < howItWorksSteps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-full w-full h-0.5 bg-white/20 z-0">
                      <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    </div>
                  )}
                  <Card className="relative z-10 bg-white/10 border-white/20 backdrop-blur-sm hover:bg-white/15 transition-all duration-300 h-full">
                    <CardContent className="p-6 text-center">
                      <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-5 mx-auto">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div className="text-xs font-medium text-emerald-400 mb-2">Step {item.step}</div>
                      <h3 className="text-base font-semibold text-white mb-3">{item.title}</h3>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/how-it-works">
              <Button size="lg" className="bg-white text-[#1e3a5f] hover:bg-slate-100 px-8 py-6 text-base transition-all duration-300">
                See the Full Process
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
              Who This Is For
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Founders and teams who want IP protection without premature commitment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audienceTypes.map((audience) => {
              const Icon = iconMap[audience.icon];
              return (
                <Link key={audience.id} to={audience.link}>
                  <Card className="bg-white border-slate-200 hover:border-[#1e3a5f]/30 hover:shadow-xl transition-all duration-300 h-full group cursor-pointer">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#1e3a5f] transition-colors duration-300">
                        <Icon className="w-6 h-6 text-[#1e3a5f] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#0f1f2e] mb-2 group-hover:text-[#1e3a5f] transition-colors duration-300">{audience.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{audience.description}</p>
                      <div className="flex items-center text-[#1e3a5f] text-sm font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Learn more <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0f1f2e] mb-4">
            Questions?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            We've answered the most common questions founders ask about this model.
          </p>
          <Link to="/how-it-works">
            <Button variant="outline" size="lg" className="border-slate-300 text-slate-700 hover:bg-slate-50">
              Read the FAQ
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-[#0f1f2e] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#1e3a5f]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Start Protecting Without<br />Over-Committing
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Low early investment. Costs scale with success. Flexibility preserved throughout.
          </p>
          <Link to="/request-access">
            <Button size="lg" className="bg-white text-[#0f1f2e] hover:bg-slate-100 px-10 py-6 text-base font-semibold shadow-xl transition-all duration-300 hover:-translate-y-0.5">
              Request Evaluation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <p className="text-slate-400 text-sm mt-6">
            Not a law firm. Not a lender. Not seeking equity.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
